import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AiroService } from "../../services/airo.service";
import { ToastrService } from "ngx-toastr";
import { PageEvent } from "@angular/material/paginator";
import { DatePipe } from "@angular/common";
import { QnaGraphComponent } from "../qna-graph/qna-graph.component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

@Component({
  selector: "app-airo-data-dialog",
  templateUrl: "./airo-data-dialog.component.html",
  styleUrls: ["./airo-data-dialog.component.scss"],
  providers: [DatePipe],
})
export class AiroDataDialogComponent implements OnInit {
  userQuery = "";
  response: any;
  isLoading = false;
  pageSize: number = 20;
  page: number = 1;
  queryResults: any;
  tableColumns: any = [];
  recordsCount: number = 0;
  convo: any;
  @ViewChild("qnaGraphComponent") qnaGraphComponent!: QnaGraphComponent;

  constructor(
    public dialogRef: MatDialogRef<any>,
    private readonly airoService: AiroService,
    private readonly toastrService: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.userQuery = this.data.query;
    this.generateAnswer();
  }

  getAnswerData(answerId: number) {
    this.airoService
      .getAnswerResults(this.data.repoId, answerId, {
        page: this.page,
        number_of_records: this.pageSize,
      })
      .subscribe((res: any) => {
        if (res.success) {
          this.queryResults = res.data;
          this.convo.queryResults = res.data;
          this.convo.recordsCount = res.total_records;
          if (this.queryResults.length) {
            this.tableColumns = Object.keys(this.queryResults[0]);
          } else {
            this.tableColumns = [];
          }
          this.convo.tableColumns = this.tableColumns;
          this.recordsCount = res.total_records;
        } else {
          this.toastrService.error(res.message);
        }
        this.isLoading = false;
      });
  }

  generateAnswer() {
    this.isLoading = true;
    this.airoService
      .generateAnswer(this.data.repoId, this.userQuery)
      .subscribe((resp: any) => {
        this.response = resp.data;
        this.convo = {
          question: this.response.user_query,
          answerId: this.response.id,
          projectId: this.response.project_id,
          query: this.response.sql,
          suggestedQuestions: this.response.suggested_questions,
          canGeneratePlot: this.response.can_generate_plot,
          columnsMetadata: this.response.columns_metadata,
          empty_response_message: this.response.empty_response_message,
          chart_options: this.response?.chart_options || {},
          available_charts: this.response?.available_charts || [],
        };
        this.getAnswerData(resp.data.id);
      });
  }

  handlePageChange(event: PageEvent) {
    this.isLoading = true;
    this.airoService
      .getAnswerResults(this.data.repoId, this.response.id, {
        page: event.pageIndex + 1,
        number_of_records: event.pageSize,
      })
      .subscribe((res: any) => {
        if (res.success) {
          this.pageSize = event.pageSize;
          this.page = event.pageIndex + 1;
          this.queryResults = res.data;
        } else {
          this.toastrService.error(res.message);
        }
        this.isLoading = false;
      });
  }

  downloadCSV(table: HTMLTableElement) {
    const rows = Array.from(table.rows);
    const csvContent = rows
      .map((row) => {
        const cols = Array.from(row.cells);
        return cols
          .map((col) => {
            // Escape any double quotes in data
            const cellText = col.innerText.replace(/"/g, '""');
            // Wrap value in quotes to preserve commas
            return `"${cellText}"`;
          })
          .join(",");
      })
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async exportChart() {
    if (this.convo.canGeneratePlot) {
      await this.qnaGraphComponent.exportChart();
    } else {
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        let yPosition = 20;
        const margin = 20;

        const timestamp =
          this.datePipe.transform(
            new Date(),
            "MMM dd, yyyy HH:mm:ss 'UTC'",
            "UTC"
          ) || "";

        // Footer helper
        const addFooter = (doc: any, timestamp: string) => {
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;

          const footerMargin = 10;
          const message = `This PDF is digitally generated and confidential. Generated on ${timestamp}`;
          const pageText = `Page #${pageNumber}`;

          doc.setFontSize(6);
          doc.setTextColor(100);
          doc.setFont("helvetica", "normal");

          doc.text(message, pageWidth / 2, pageHeight - footerMargin, {
            align: "center",
          });

          doc.text(pageText, pageWidth - 10, pageHeight - footerMargin, {
            align: "right",
          });
        };

        const originalAddPage = pdf.addPage;
        pdf.addPage = function () {
          originalAddPage.call(this);
          addFooter(this, timestamp);
          return this;
        };

        const logo = new Image();
        logo.src = "assets/images/pdf-logo.png";
        pdf.addImage(logo, "PNG", pageWidth - margin - 30, 5, 30, 20);
        const airoLogo = new Image();
        airoLogo.src = "assets/images/airo-black.png";
        pdf.addImage(airoLogo, "PNG", margin, 12, 30, 8);

        // Add title — centered
        pdf.setTextColor(0, 102, 204);
        pdf.setFontSize(16);
        pdf.setFont("helvetica", "bold");
        const title = "Airo Data Report";
        const titleLines = pdf.splitTextToSize(title, pageWidth - 40);
        pdf.text(titleLines, pageWidth / 2, yPosition, { align: "center" });
        yPosition += titleLines.length * 7 + 10;

        // Add query — sanitize special characters and newlines before splitting ✅
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");

        const rawQuery = "Query: " + this.convo.question;
        const sanitizedQuery = rawQuery
          .replace(/\r\n/g, " ") // Windows newlines
          .replace(/\n/g, " ") // Unix newlines
          .replace(/\r/g, " ") // Carriage returns
          .replace(/\t/g, " ") // Tabs
          .replace(/\s+/g, " ") // Collapse multiple spaces
          .trim();

        const queryLines = pdf.splitTextToSize(sanitizedQuery, pageWidth - 40);
        pdf.text(queryLines, 20, yPosition);
        yPosition += queryLines.length * 7 + 10; // ✅ accurate after sanitization

        // Prepare table data
        let tableColumns =
          this.convo.tableColumns ||
          Object.keys(this.convo.queryResults[0] || {});

        const tableRows = this.convo.queryResults.map((row: any) => {
          return tableColumns.map((col: string) => {
            const value = row[col];
            if (typeof value === "number") {
              return value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            }
            return value != null ? String(value) : "";
          });
        });

        // Push to new page if not enough room for the table ✅
        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = 20;
        }

        // Add table title
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text("Data Table", 20, yPosition);
        yPosition += 7;

        tableColumns = tableColumns.map((col: any) => {
          if (this.convo.columnsMetadata[col]?.unit) {
            return `${col} (${this.convo.columnsMetadata[col]?.unit})`;
          }
          return col;
        });

        // Add table to PDF
        autoTable(pdf, {
          head: [tableColumns],
          body: tableRows,
          startY: yPosition,
          theme: "grid",
          styles: {
            fontSize: 8,
            cellPadding: 2,
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          margin: { left: 20, right: 20 },
          didDrawPage: function () {
            addFooter(pdf, timestamp);
          },
        });

        // Generate filename
        const timestamp_file = this.datePipe.transform(
          new Date(),
          "yyyyMMdd_HHmm"
        );

        const filename = `${
          this.sanitizeFilename(this.convo.question) || "chart-export"
        }_${timestamp_file}`;
        pdf.save(`${filename}.pdf`);

        console.log("PDF exported successfully");
      } catch (error) {
        console.error("Error exporting chart:", error);
      }
    }
  }

  private sanitizeFilename(filename: string): string {
    if (!filename) return "chart-export";
    // Remove invalid characters and limit length
    return filename
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase()
      .substring(0, 50);
  }

  close() {
    this.dialogRef.close();
  }
}
